/**
 * The charges to the reseller's card (TMMRSL-0046, TMMAPI-0098), newest first: when, what for
 * ("Minimum shortfall, August 2026" / "Negative month, …"), the amount in the charge's own
 * currency, the status in words, the attempt, the next daily retry of a failed charge, and notes —
 * the reason in the console's words (never the raw code), "Payouts on hold" once a charge has
 * failed for 7 days, and the OWNER's "Add your card again" for a charge blocked by the bank or a
 * missing card (TMMAPI-0102: re-verifying is always adding the card again, never removing it).
 * The reseller has no retry route; only the product admin retries a charge by hand.
 */
import { useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { ResellerChargeItem } from '@/api/resellerBilling';
import { useIsOwner } from '@/auth/authHooks';
import { ApiErrorState } from '@/components/ApiErrorState';
import { MoneyCell, NumericCell } from '@/components/cells';
import { StatusChip } from '@/components/status/StatusChip';
import type { RemoteState } from '@/components/useRemote';
import { DATE_ONLY, formatCivilMonth, formatInstantOrDash } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { AddCardDialog } from './AddCardDialog';
import { needsCardAgain } from './openCharges';

function chargeFor(charge: ResellerChargeItem, t: TFunction, locale: string, fallback: string) {
  if (charge.kind === null || charge.periodStart === null) return fallback;
  try {
    return t(`resellerBilling.charges.kind.${charge.kind}`, {
      month: formatCivilMonth(charge.periodStart, locale),
    });
  } catch {
    return fallback;
  }
}

function reason(failureCode: string | null, t: TFunction): string | null {
  if (failureCode === null) return null;
  if (failureCode === 'NO_CARD' || failureCode === 'REVERIFY_CARD') {
    return t(`resellerBilling.charges.reason.${failureCode}`);
  }
  return t('resellerBilling.charges.reason.other');
}

export interface ChargesSectionProps {
  charges: RemoteState<ResellerChargeItem[]>;
  hasOpenCharges: boolean;
  onChanged: () => void;
}

export function ChargesSection({ charges, hasOpenCharges, onChanged }: ChargesSectionProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const isOwner = useIsOwner();
  const [reverifying, setReverifying] = useState(false);
  const dash = t('statements.unavailable');
  const title = t('resellerBilling.charges.title');

  return (
    <Paper sx={{ p: 2.5 }}>
      <Typography variant="subtitle1" component="h2" sx={{ mb: 1.5 }}>
        {title}
      </Typography>
      {charges.status === 'loading' ? (
        <Box role="status" aria-label={t('common.loading')}>
          <Skeleton variant="rounded" height={160} />
        </Box>
      ) : null}
      {charges.status === 'error' ? (
        <ApiErrorState what={t('errors.what.charges')} error={charges.error} onRetry={charges.reload} />
      ) : null}
      {charges.status === 'ready' && (charges.data ?? []).length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('resellerBilling.charges.empty')}
        </Typography>
      ) : null}
      {charges.status === 'ready' && charges.data && charges.data.length > 0 ? (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label={title}>
            <TableHead>
              <TableRow>
                <TableCell>{t('resellerBilling.charges.col.created')}</TableCell>
                <TableCell>{t('resellerBilling.charges.col.for')}</TableCell>
                <TableCell align="right">{t('resellerBilling.charges.col.amount')}</TableCell>
                <TableCell>{t('resellerBilling.charges.col.status')}</TableCell>
                <TableCell align="right">{t('resellerBilling.charges.col.attempt')}</TableCell>
                <TableCell>{t('resellerBilling.charges.col.nextRetry')}</TableCell>
                <TableCell>{t('resellerBilling.charges.col.notes')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {charges.data.map((charge) => {
                const why = reason(charge.failureCode, t);
                return (
                  <TableRow key={charge.id}>
                    <TableCell component="th" scope="row">
                      {formatInstantOrDash(charge.createdAt, locale, dash, DATE_ONLY)}
                    </TableCell>
                    <TableCell>{chargeFor(charge, t, locale, dash)}</TableCell>
                    <MoneyCell minor={charge.amountMinor} currency={charge.currency} />
                    <TableCell>
                      <StatusChip domain="resellerCharge" status={charge.status} />
                    </TableCell>
                    <NumericCell value={charge.attempt} />
                    <TableCell>
                      {charge.status === 'FAILED'
                        ? formatInstantOrDash(charge.nextRetryAt, locale, dash, DATE_ONLY)
                        : dash}
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          gap: 0.75,
                        }}
                      >
                        {why ? <Typography variant="body2">{why}</Typography> : null}
                        {charge.holdsPayouts ? (
                          <StatusChip domain="hold" status="PAYOUTS_HELD" />
                        ) : null}
                        {isOwner && needsCardAgain(charge) ? (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setReverifying(true)}
                          >
                            {t('resellerBilling.charges.addAgain')}
                          </Button>
                        ) : null}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      ) : null}
      <AddCardDialog
        open={reverifying}
        mode="reverify"
        hasOpenCharges={hasOpenCharges}
        onClose={() => setReverifying(false)}
        onDone={onChanged}
      />
    </Paper>
  );
}
